import React from 'react';
import { 
  Sparkles, Loader2, CheckCircle, AlertTriangle, X, 
  ChevronUp, Check, ArrowRight, Layers
} from 'lucide-react';
import { useAiHeavyJob } from '../../context/AiHeavyJobContext';
import { getFriendlyErrorMessage } from '../../utils/aiErrorUtils';

export const AiGlobalFloatingWidget: React.FC = () => {
  const {
    activeJob,
    jobEvent,
    isProcessing,
    isCompleted,
    isFailed,
    isModalOpen,
    isMinimized,
    completedQuestions,
    maximizeModal,
    dismissJob
  } = useAiHeavyJob();

  // Widget ở góc dưới BẮT BUỘC chỉ hiện khi isMinimized = true VÀ có dữ liệu/tiến trình AI
  const hasTask = Boolean(activeJob || isProcessing || isCompleted || completedQuestions.length > 0 || jobEvent);
  if (!isMinimized || !hasTask || isModalOpen) {
    return null;
  }

  const progress = Math.min(100, Math.max(0, jobEvent?.progress || (isCompleted ? 100 : 15)));
  const questionsCount = completedQuestions.length || (Array.isArray(jobEvent?.questions) ? jobEvent.questions.length : 0);

  return (
    <div 
      className="fixed bottom-5 right-5 z-50 max-w-sm sm:max-w-md w-full animate-in fade-in slide-in-from-bottom-4 duration-300"
      style={{ pointerEvents: 'auto' }}
    >
      <div 
        onClick={maximizeModal}
        className={`bg-white rounded-2xl shadow-2xl border transition-all overflow-hidden cursor-pointer group hover:scale-[1.02] active:scale-[0.99] ${
          isCompleted 
            ? 'border-emerald-300 shadow-emerald-500/20 ring-2 ring-emerald-500/20' 
            : isFailed 
              ? 'border-rose-300 shadow-rose-500/20 ring-2 ring-rose-500/20' 
              : 'border-purple-300 shadow-purple-500/20 ring-2 ring-purple-500/20'
        }`}
      >
        {/* Header / Main Bar */}
        <div className={`p-3.5 flex items-center justify-between transition-colors ${
          isCompleted 
            ? 'bg-emerald-50 text-emerald-950' 
            : isFailed 
              ? 'bg-rose-50 text-rose-950' 
              : 'bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 text-purple-950'
        }`}>
          <div className="flex items-center space-x-3 min-w-0">
            {/* Status Icon */}
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-xs transition-transform ${
              isCompleted 
                ? 'bg-emerald-600 text-white group-hover:scale-110' 
                : isFailed 
                  ? 'bg-rose-600 text-white' 
                  : 'bg-purple-600 text-white animate-pulse'
            }`}>
              {isCompleted ? (
                <Check className="w-5 h-5 stroke-[2.5]" />
              ) : isFailed ? (
                <AlertTriangle className="w-5 h-5" />
              ) : (
                <Loader2 className="w-5 h-5 animate-spin" />
              )}
            </div>

            {/* Info Text */}
            <div className="min-w-0">
              <div className="flex items-center space-x-2">
                <h4 className="text-xs font-black uppercase tracking-wider truncate">
                  {isCompleted 
                    ? 'Đã tạo xong câu hỏi!' 
                    : isFailed 
                      ? 'Tạo câu hỏi thất bại' 
                      : 'Đang tạo câu hỏi...'}
                </h4>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                  isCompleted 
                    ? 'bg-emerald-200 text-emerald-900 font-bold' 
                    : isFailed 
                      ? 'bg-rose-200 text-rose-800 font-bold' 
                      : 'bg-purple-200 text-purple-900 font-bold'
                }`}>
                  {isCompleted ? `${questionsCount} câu` : `${progress}%`}
                </span>
              </div>
              
              <p className="text-[11px] text-slate-500 truncate mt-0.5">
                {isCompleted 
                  ? 'Nhấn vào đây để xem & lưu vào bộ' 
                  : isFailed 
                    ? getFriendlyErrorMessage(jobEvent?.errorMessage || jobEvent?.errorCode) 
                    : (jobEvent?.message || 'Gemini đang phân tích nội dung...')}
              </p>
            </div>
          </div>

          {/* Action Icons */}
          <div className="flex items-center space-x-1 shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={maximizeModal}
              className="p-1.5 text-slate-400 hover:text-purple-700 hover:bg-purple-100/50 rounded-lg transition-colors cursor-pointer"
              title="Phóng to hộp thoại"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            {(isCompleted || isFailed) && (
              <button
                type="button"
                onClick={dismissJob}
                className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                title="Đóng thông báo"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Progress Bar Line */}
        {!isCompleted && !isFailed && (
          <div className="w-full bg-slate-100 h-1.5 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 transition-all duration-500 animate-pulse"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
